import { Injectable } from '@angular/core';
import {
  EsriExecute,
  EsriQuery,
  EsriRelationshipQuery,
  EsriStatisticDefinition,
} from '../../config/esri.module';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class QueryService {
  constructor() {}

  /**
   * @function buildQuery
   * @param requiredGeometry
   * @param whereQuery
   * @param outFields
   * @param orderByFields
   * @param queryExtent
   * @param offset
   * @param pageSize
   * @returns {__esri.Query}
   * @description Erstellt ein Esri Query mit den gegebenen Kriterias
   */
  public buildQuery(
    requiredGeometry: boolean,
    whereQuery?: string,
    outFields?: string[],
    orderByFields?: string[],
    queryExtent?: __esri.Geometry,
    offset = null,
    pageSize = null
  ): __esri.Query {
    const query = new EsriQuery();
    query.returnGeometry = requiredGeometry;
    outFields ? (query.outFields = outFields) : (query.outFields = ['*']);
    whereQuery ? (query.where = whereQuery) : (query.where = null);
    orderByFields ? (query.orderByFields = orderByFields) : (query.orderByFields = null);
    queryExtent ? (query.geometry = queryExtent) : (query.geometry = null);
    query.num = pageSize;
    query.start = offset;
    return query;
  }

  /**
   * @function buildIntersectsQuery
   * @param geometry
   * @param requiredGeometry
   * @param whereQuery
   * @returns {__esri.Query}
   * @description Erstellt ein Esri Query mit den gegebenen Kriterias
   */
  public buildIntersectsQuery(
    geometry: __esri.Geometry,
    requiredGeometry: boolean,
    whereQuery?: string
  ): __esri.Query {
    const query: __esri.Query = this.buildQuery(requiredGeometry, whereQuery);
    query.geometry = geometry;
    query.spatialRelationship = 'intersects';
    return query;
  }

  public buildContainsGeometryQuery(
    geometry: __esri.Geometry,
    requiredGeometry: boolean,
    whereQuery?: string
  ): __esri.Query {
    const query: __esri.Query = this.buildQuery(requiredGeometry, whereQuery);
    query.geometry = geometry;
    query.spatialRelationship = 'within';
    return query;
  }

  /**
   * @function execute
   * @param url
   * @param query
   * @param errorCode
   * @returns observable
   * @description führt eine query aus und gibt die resultate als promise zurück
   */
  public execute(url: string, query: __esri.Query, errorCode: any): Observable<any> {
    return this.executeByQuery(url, query, errorCode);
  }

  public executeForCount(
    url: string,
    query: __esri.Query,
    errorCode: any
  ): Observable<number | any> {
    return from(
      EsriExecute.executeForCount(url, query)
        .then((result: number) => result)
        .catch(error => ({ type: errorCode, response: error }))
    );
  }

  public executeByQuery(
    url: string,
    query: __esri.Query,
    errorCode: any
  ): Observable<__esri.FeatureSet> {
    return from(
      EsriExecute.executeQueryJSON(url, query)
        .then((result: any) => result)
        .catch(error => ({ type: errorCode, response: error }))
    );
  }

  /**
   * @function executeRelationshipQuery
   * @returns observable
   * @description führt eine relationship queries aus und gibt die resultate als promise zurück
   * @param url
   * @param params
   * @param errorCode
   */
  public executeRelationshipQuery(
    url: string,
    params: __esri.RelationshipQuery,
    errorCode: any
  ): Observable<any> {
    return from(
      EsriExecute.executeRelationshipQuery(url, params)
        .then((result: any) => {
          return result;
        })
        .catch(error => {
          return { type: errorCode, response: error };
        })
    );
  }

  /**
   * @function buildRelationshipQuery
   * @param props: properties object
   * @returns {__esri.RelationshipQuery}
   * @description Erstellt ein Esri RelationshipQuery mit den gegebenen Properties
   */
  public buildRelationshipQuery(props: any): __esri.RelationshipQuery {
    const esriRelationShipQuery = new EsriRelationshipQuery();
    esriRelationShipQuery.objectIds = props.objectIds;
    esriRelationShipQuery.outFields = ['*'];
    esriRelationShipQuery.relationshipId = props.relationshipId;
    esriRelationShipQuery.returnGeometry = props.returnGeometry;

    return esriRelationShipQuery;
  }

  /**
   * @function buildStatisticQuery
   * @returns {__esri.Query}
   * @description Erstellt ein Esri Query mit den gegebenen Kriterias für statistiken
   * @param stType
   * @param stField
   * @param outStatFieldName
   */
  public buildStatisticQuery(
    stType: string,
    stField: string,
    outStatFieldName: string
  ): __esri.Query {
    const query: __esri.Query = this.buildQuery(false);
    // create properties
    const stDefProp: any = {
      statisticType: stType,
      onStatisticField: stField,
      outStatisticFieldName: outStatFieldName,
    };
    const statisticDef = new EsriStatisticDefinition(stDefProp);
    query.outStatistics = [statisticDef];
    return query;
  }

  public executeForDomainValues(url: string, outFields = ['*'], where = '1=1'): Observable<any> {
    const query = new EsriQuery();
    query.where = where;
    query.outFields = outFields;
    query.returnGeometry = false;
    query.returnDistinctValues = true;

    return this.executeByQuery(url, query, 'execute for Doamin values');
  }

  /**
   * @function executeForId
   * @description Get total count for given query
   * @param url
   * @param where
   * @param orderByFields
   * @param startNum
   * @param featureNum
   */
  public executeForId(
    url: string,
    where: string = '1=1',
    orderByFields: string[] = [],
    startNum?: number,
    featureNum?: number
  ): Observable<number[]> {
    const query = new EsriQuery();
    query.returnQueryGeometry = false;
    query.where = where;
    query.orderByFields = orderByFields;
    query.outFields = ['OBJECTID'];
    if (startNum) query.start = startNum;
    if (featureNum) query.num = featureNum;
    return this.executeByQuery(url, query, 'executeForId').pipe(
      map(featureset => featureset.features.map(x => x.attributes.OBJECTID))
    );
  }
}
